// Minimale ZIP-schrijver, zonder dependency.
//
// Waarom: een knop die meerdere bestanden achter elkaar downloadt, werkt niet.
// De browser staat één automatische download per klik toe en blokkeert de rest
// (Chrome vraagt "meerdere bestanden downloaden?" en zonder toestemming komt er
// niets). Gemeten op de Revenue-pagina: "Export All" probeerde drie bestanden,
// alle drie zonder fout in de code, en er kwamen er twee aan. Eén ZIP is één
// download en dus altijd volledig.
//
// Methode "store" (geen compressie): CSV-tekst comprimeert weliswaar goed, maar
// deflate zelf schrijven is een veelvoud aan code en risico. Een export van een
// paar MB is met store nog steeds één klein bestand.

// CRC-32 (IEEE 802.3), de checksum die het ZIP-formaat per bestand verwacht.
let crcTabel = null;
function tabel() {
  if (crcTabel) return crcTabel;
  crcTabel = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    crcTabel[i] = c >>> 0;
  }
  return crcTabel;
}

export function crc32(bytes) {
  const t = tabel();
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = t[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

// DOS-datum/tijd. ZIP kent geen tijdzone en geen seconden-precisie (2s-stappen).
function dosDatumTijd(d) {
  const datum = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  const tijd = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  return { datum: datum & 0xFFFF, tijd: tijd & 0xFFFF };
}

// Eigen UTF-8-encoder in plaats van TextEncoder: die bestaat niet in de
// jsdom-omgeving waarin de tests draaien, en een ZIP-schrijver zonder tests is
// precies het soort code dat stil kapot gaat.
export function utf8(str) {
  const s = String(str ?? '');
  const uit = [];
  for (let i = 0; i < s.length; i++) {
    let c = s.codePointAt(i);
    if (c > 0xFFFF) i++;                       // surrogaatpaar: tweede helft overslaan
    if (c < 0x80) uit.push(c);
    else if (c < 0x800) uit.push(0xC0 | (c >> 6), 0x80 | (c & 0x3F));
    else if (c < 0x10000) uit.push(0xE0 | (c >> 12), 0x80 | ((c >> 6) & 0x3F), 0x80 | (c & 0x3F));
    else uit.push(0xF0 | (c >> 18), 0x80 | ((c >> 12) & 0x3F), 0x80 | ((c >> 6) & 0x3F), 0x80 | (c & 0x3F));
  }
  return new Uint8Array(uit);
}

function schrijver(lengte) {
  const buf = new Uint8Array(lengte);
  let p = 0;
  return {
    buf,
    u16(v) { buf[p++] = v & 0xFF; buf[p++] = (v >>> 8) & 0xFF; },
    u32(v) { buf[p++] = v & 0xFF; buf[p++] = (v >>> 8) & 0xFF; buf[p++] = (v >>> 16) & 0xFF; buf[p++] = (v >>> 24) & 0xFF; },
    bytes(b) { buf.set(b, p); p += b.length; },
    get pos() { return p; },
  };
}

/**
 * Bouwt een ZIP uit [{ name, content }] en geeft de rúwe bytes terug.
 * `content` is tekst (UTF-8) of al een Uint8Array.
 * `datum` is optioneel — meegeven maakt de uitvoer testbaar/reproduceerbaar.
 * Apart van makeZip zodat de tests niet van Blob/arrayBuffer afhangen.
 */
export function zipBytes(bestanden, datum = new Date()) {
  const items = (bestanden || [])
    .filter(f => f && f.name)
    .map(f => {
      const naam = utf8(f.name);
      const data = f.content instanceof Uint8Array ? f.content : utf8(f.content ?? '');
      return { naam, data, crc: crc32(data), offset: 0 };
    });
  const { datum: dd, tijd: dt } = dosDatumTijd(datum);

  const lokaalTotaal = items.reduce((s, it) => s + 30 + it.naam.length + it.data.length, 0);
  const centraalTotaal = items.reduce((s, it) => s + 46 + it.naam.length, 0);
  const w = schrijver(lokaalTotaal + centraalTotaal + 22);

  for (const it of items) {
    it.offset = w.pos;
    w.u32(0x04034B50);          // local file header
    w.u16(20); w.u16(0);        // versie 2.0, geen vlaggen
    w.u16(0);                   // methode 0 = store
    w.u16(dt); w.u16(dd);
    w.u32(it.crc);
    w.u32(it.data.length);      // gecomprimeerd = ongecomprimeerd bij store
    w.u32(it.data.length);
    w.u16(it.naam.length); w.u16(0);
    w.bytes(it.naam);
    w.bytes(it.data);
  }

  const centraalStart = w.pos;
  for (const it of items) {
    w.u32(0x02014B50);          // central directory header
    w.u16(20); w.u16(20); w.u16(0);
    w.u16(0);
    w.u16(dt); w.u16(dd);
    w.u32(it.crc);
    w.u32(it.data.length); w.u32(it.data.length);
    w.u16(it.naam.length); w.u16(0); w.u16(0);
    w.u16(0); w.u16(0); w.u32(0);
    w.u32(it.offset);
    w.bytes(it.naam);
  }

  // Grootte van de central directory NU vastleggen: `w.pos` loopt hieronder door
  // terwijl we de EOCD schrijven, en dan klopt het verschil niet meer.
  const centraalLengte = w.pos - centraalStart;

  w.u32(0x06054B50);            // end of central directory
  w.u16(0); w.u16(0);
  w.u16(items.length); w.u16(items.length);
  w.u32(centraalLengte);
  w.u32(centraalStart);
  w.u16(0);

  return w.buf;
}

/** Zelfde als zipBytes, maar als Blob — klaar om te downloaden. */
export function makeZip(bestanden, datum = new Date()) {
  return new Blob([zipBytes(bestanden, datum)], { type: 'application/zip' });
}
